import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { Authenticated } from "@refinedev/core";

// Layout Components
import { MainLayout } from "@/components/layout/MainLayout";

// Pages
import { LoginPage } from "@/pages/auth/LoginPage";
import { LoginPageOAuth2 } from "@/pages/auth/LoginPageOAuth2";
import { CallbackPage } from "@/pages/auth/CallbackPage";
import { authConfig } from "@/services/authConfig";
import { DashboardPage } from "@/pages/dashboard/DashboardPage";
import { ContentList } from "@/pages/content/ContentList";
import { ContentCreate } from "@/pages/content/ContentCreate";
import { ContentEdit } from "@/pages/content/ContentEdit";
import { UserList } from "@/pages/users/UserList";
import { ArtistList } from "@/pages/artists/ArtistList";
import { ActorCreate } from "@/pages/artists/ActorCreate";
import { ActorEdit } from "@/pages/artists/ActorEdit";
import { ActorShow } from "@/pages/artists/ActorShow";
import { NotificationList } from "@/pages/notifications/NotificationList";
import { CategoryList } from "@/pages/categories/CategoryList";
import { UserCreate } from "@/pages/users/UserCreate";
import { UserEdit } from "@/pages/users/UserEdit";
import { UserShow } from "@/pages/users/UserShow";
import { GenreCreate } from "@/pages/categories/GenreCreate";
import { GenreEdit } from "@/pages/categories/GenreEdit";
import { GenreShow } from "@/pages/categories/GenreShow";
import { ContentShow } from "@/pages/content/ContentShow";
import { TypeList } from "@/pages/types/TypeList";
import { TypeCreate } from "@/pages/types/TypeCreate";
import { TypeEdit } from "@/pages/types/TypeEdit";
import { TypeShow } from "@/pages/types/TypeShow";
import { StatisticsPage } from "@/pages/statistics/StatisticsPage";
import { NotificationCreate } from "@/pages/notifications/NotificationCreate";
import { NotificationEdit } from "@/pages/notifications/NotificationEdit";
import { SubscriptionsPage } from "@/pages/subscriptions/SubscriptionsPage";
import { TechnicalSettingsPage } from "@/pages/settings/TechnicalSettingsPage";
import { AccountLimitsPage } from "@/pages/settings/AccountLimitsPage";
import { CommentModerationPage } from "@/pages/comments/CommentModerationPage";
import { ABTestsPage } from "@/pages/abtests/ABTestsPage";

export const App: React.FC = () => {
  return (
    <Routes>
      <Route path="/login" element={authConfig.isKeycloak ? <LoginPageOAuth2 /> : <LoginPage />} />
      <Route path="/auth/callback" element={<CallbackPage />} />

      <Route
        path="/"
        element={
          <Authenticated
            key="authenticated-routes"
            fallback={<Navigate to="/login" replace />}
            loading={
              <div style={{ padding: "20px", textAlign: "center" }}>
                Chargement...
              </div>
            }
          >
            <MainLayout />
          </Authenticated>
        }
      >
        <Route index element={<DashboardPage />} />

        {/* Content Management */}
        <Route path="content" element={<ContentList />} />
        <Route path="content/create" element={<ContentCreate />} />
        <Route path="content/edit/:id" element={<ContentEdit />} />
        <Route path="content/show/:id" element={<ContentShow />} />

        {/* User Management */}
        <Route path="users" element={<UserList />} />
        <Route path="users/create" element={<UserCreate />} />
        <Route path="users/edit/:id" element={<UserEdit />} />
        <Route path="users/show/:id" element={<UserShow />} />

        {/* Artist Management */}
        <Route path="artists" element={<ArtistList />} />
        <Route path="artists/create" element={<ActorCreate />} />
        <Route path="artists/edit/:id" element={<ActorEdit />} />
        <Route path="artists/show/:id" element={<ActorShow />} />

        {/* Notifications */}
        <Route path="notifications" element={<NotificationList />} />
        <Route path="notifications/create" element={<NotificationCreate />} />
        <Route path="notifications/edit/:id" element={<NotificationEdit />} />

        {/* Categories */}
        <Route path="categories" element={<CategoryList />} />
        <Route path="categories/create" element={<GenreCreate />} />
        <Route path="categories/edit/:id" element={<GenreEdit />} />
        <Route path="categories/show/:id" element={<GenreShow />} />

        {/* Types de contenu */}
        <Route path="types" element={<TypeList />} />
        <Route path="types/create" element={<TypeCreate />} />
        <Route path="types/edit/:id" element={<TypeEdit />} />
        <Route path="types/show/:id" element={<TypeShow />} />

        {/* Other routes */}
        <Route path="subscriptions" element={<SubscriptionsPage />} />
        <Route path="statistics" element={<StatisticsPage />} />
        <Route path="technical-settings" element={<TechnicalSettingsPage />} />
        <Route path="account-limits" element={<AccountLimitsPage />} />
        <Route path="comment-moderation" element={<CommentModerationPage />} />
        <Route path="ab-tests" element={<ABTestsPage />} />

        {/* Catch all */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
};
